import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CustomerFormComponent } from '../customer-form/customer-form.component';
import { CustomerService } from '../../../services/customer.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Customer } from '../../../models/customer';
import { CustomerUpdateRequest } from '../../../models/api/customerUpdateRequest';
import { IpRange } from '../../../models/ipRange';

@Component({
  selector: 'app-customer-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CustomerFormComponent,
    NgxSpinnerModule],
  templateUrl: './customer-edit.component.html',
  styleUrls: ['./customer-edit.component.scss']
})
export class CustomerEditComponent implements OnInit {
  @ViewChild(CustomerFormComponent) customerFormComponent?: CustomerFormComponent;

  form: FormGroup;
  customerId: string | null = null;
  initialSelectedCompanies: Array<{ id: string; companyName: string }> = [];
  initialIpRanges: IpRange[] = [];
  private loadedCustomer: Customer | null = null;

  constructor(
    private fb: FormBuilder,
    private customerService: CustomerService,
    private router: Router,
    private route: ActivatedRoute,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {
    this.form = this.fb.group({
      fullName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      contactPhone1: ['', Validators.required],
      contactPhone2: [''],
      communicationsManager: [''],
      accessStartDate: [''],
      accessEndDate: [''],
      canAccessDirectors: [false],
      isInternal: [false],
      reportVariables: [[]],
      active: [true],
      companies: [[]],
      countries: [[]],
      industries: [[]],
      ipRanges: [[]],
      selectedMeasurements: [[]]
    });
  }

  ngOnInit() {
    this.customerId = this.route.snapshot.paramMap.get('id');
    if (!this.customerId) {
      return;
    }

    this.spinnerService.show();
    this.customerService.getCustomer(this.customerId).subscribe({
      next: (customer) => {
        this.loadedCustomer = customer;
        this.initialSelectedCompanies = customer.companyAccess?.map(c => ({
          id: c.companyId,
          companyName: c.companyName ?? c.companyId
        })) ?? [];
        this.initialIpRanges = customer.allowedIpRanges?.map(r => ({ start: r.start, end: r.end })) ?? [];
        this.form.patchValue({
          fullName: customer.fullName,
          email: customer.email,
          contactPhone1: customer.contactPhone1,
          contactPhone2: customer.contactPhone2,
          communicationsManager: customer.communicationsManager,
          accessStartDate: customer.accessStartDate,
          accessEndDate: customer.accessEndDate,
          canAccessDirectors: customer.canAccessDirectors,
          isInternal: customer.isInternal,
          active: customer.isActive,
          countries: customer.countries?.map(c => c.id) ?? [],
          industries: customer.industries?.map(i => i.id) ?? [],
          companies: customer.companyAccess?.map(c => c.companyId) ?? [],
          reportVariables: customer.reportVariableCategories?.map(c => c.id) ?? [],
          selectedMeasurements: (customer.accessToYears ?? []).map(y => y.toString())
        });
        this.spinnerService.hide();
      },
      error: (error: Error) => {
        console.error('Error loading customer:', error);
        this.spinnerService.hide();
      }
    });
  }

  onSubmit() {
    if (!this.form.valid || !this.customerId || !this.loadedCustomer) {
      return;
    }

    const v = this.form.value;
    const request: CustomerUpdateRequest = {
      email: v.email,
      fullName: v.fullName,
      userName: v.email,
      role: this.loadedCustomer.role,
      accessToYears: (v.selectedMeasurements ?? []).map((y: string) => parseInt(y, 10)),
      isActive: !!v.active,
      contactPhone1: v.contactPhone1 ?? '',
      contactPhone2: v.contactPhone2 ?? '',
      communicationsManager: v.communicationsManager ?? '',
      accessStartDate: v.accessStartDate,
      accessEndDate: v.accessEndDate,
      canAccessDirectors: !!v.canAccessDirectors,
      isInternal: !!v.isInternal,
      reportVariableCategoryIds: v.reportVariables ?? [],
      countryIds: v.countries ?? [],
      industryIds: v.industries ?? [],
      companyIds: v.companies ?? [],
      ipRanges: this.customerFormComponent?.ipRanges ?? []
    };

    this.spinnerService.show();
    this.customerService.updateCustomer(this.customerId, request).subscribe({
      next: () => {
        this.spinnerService.hide();
        this.toast.success('Customer updated successfully', {
          iconTheme: {
            primary: 'var(--mat-sys-primary)',
            secondary: '#FFF'
          }
        });
        this.router.navigate(['/customers']);
      },
      error: (error: Error) => {
        this.spinnerService.hide();
        console.error('Error updating customer:', error);
      }
    });
  }

  onCancel() {
    this.router.navigate(['/customers']);
  }
}
